import imageUrlBuilder from "@sanity/image-url";
import React from "react";

import { BreakoutWidth } from "@/components/BreakoutWidth";
import { Anchor } from "@/components/elements/Anchor";
import { Media } from "@/components/elements/Media";
import { Text } from "@/components/elements/Text";
import { ExplorePafyllSection } from "@/components/ExplorePafyllSection";
import { FAQSanityCMS } from "@/components/FAQSanityCMS";
import { HeroBanner } from "@/components/HeroBanner";
import { HowItWorks } from "@/components/HowItWorks";
import styles from "@/components/LandingPage/styles.module.css";
import { PageLayout } from "@/components/layouts";
import { NotificationBanner } from "@/components/NotificationBanner";
import { PaddingWrapper } from "@/components/PaddingWrapper";
import { ProductsSection } from "@/components/ProductsSection";
import { client } from "@/sanity/lib/client";
import { FrontpageContentQueryResult } from "@/sanity/types";
import { getFrontpageContent } from "@/services/sanity";
import { I18nextLocale } from "@/types/types";
import { getAllTranslationsServerSide } from "@/utils/i18n";
import { getLocaleParts } from "@/utils/localisation-utils";

type FrontpageProps = {
  content: FrontpageContentQueryResult;
  locale: I18nextLocale;
};

export default function Index({ content, locale }: FrontpageProps) {
  // Sanity image builder
  const builder = imageUrlBuilder(client());
  function urlFor(source) {
    return builder.image(source);
  }

  return (
    <PageLayout
      title={content.title}
      description={content.description}
      titleSuffix=""
      heroContent={<HeroBanner content={content} locale={locale} />}
      onlyXPadding
    >
      <article className={styles.container}>
        {/* NOTIFICATION BANNER */}
        <NotificationBanner breakout messages={content.notification_banner} />

        {/* INTRO */}
        <BreakoutWidth as="section" className={styles.introSection}>
          <div className={styles.introImage}>
            <Media
              alt={content.featured_article_image_alt}
              noRoundedEdges
              priority
              aspectRatio="landscape"
              objectFit="cover"
              objectPosition="0% 80%"
              sizes={"(max-width: 800px) 100vw, (max-width: 992px) 50vw, 33vw"}
              src={urlFor(content.featured_article_image).width(1200).url()}
            />
          </div>
          <div className={styles.introCopy}>
            <Text
              as="h2"
              size="l"
              responsive
              className={styles.introDescription}
            >
              {content.featured_article_title}
            </Text>
            <Text className={styles.introCta}>
              <Anchor className={styles.Link} href={content.featured_cta_url}>
                {content.featured_cta_text}
              </Anchor>
            </Text>
          </div>
        </BreakoutWidth>

        {/* HOW IT WORKS */}
        <BreakoutWidth as="section" className={styles.howWorks}>
          <HowItWorks
            title={content.carousel_title}
            carousel={content.carousel_steps}
            locale={locale}
          />
        </BreakoutWidth>

        {/* ARTICLE SECTION*/}
        <ExplorePafyllSection
          title={content.article_list_title}
          articles={content.articles}
          locale={locale}
        />

        {/* FAQs */}
        <BreakoutWidth as="section" className={styles.faqSection}>
          <PaddingWrapper disable restrictInnerWidth>
            <FAQSanityCMS.Preview
              title={content.faq_title}
              faq={content.faq}
              ctaText={content.faq_cta_text}
            />
          </PaddingWrapper>
        </BreakoutWidth>

        {/* PRODUCTS SECTION */}
        <ProductsSection
          title={content.products_title}
          products={content.products}
          locale={locale}
        />
      </article>
    </PageLayout>
  );
}

export const getStaticProps = async ({ locale }) => {
  const { lang } = getLocaleParts(locale);
  const content = await getFrontpageContent(lang);

  return {
    props: {
      ...(await getAllTranslationsServerSide(locale as I18nextLocale)),
      content,
      locale,
    },
  };
};
